// frontend/src/lib/copyRender.tsx
// #1039 / AP-26 · D-CS-13, D-CS-15. Turning a stored copy value into DOM.
//
// NO SLOT IS EVER HTML, and there is no sanitiser here because there is nothing
// to sanitise: a `composed` or `linked` value is text plus OFFSETS, so the only
// thing this file can produce is that text with one `<em>` or one `<a>` around
// a range of it. Staff-authored markup would put a string typed into a text box
// through a renderer on a PRE-AUTH route; offsets make that structurally
// impossible rather than merely disallowed.
//
// Offsets rather than a second copy of the substring (D-CS-15): an empty string
// is a substring of everything, a repeated phrase is ambiguous, and the design's
// own quote normalisation is context-dependent, so a fragment normalised in
// isolation can differ from the same span inside the whole sentence.
import type { ReactNode, ComponentType } from 'react';
import type { ComposedValue, LinkedValue } from './copySlots';

export type RenderableCopy = string | ComposedValue | LinkedValue;

type LinkComponent = ComponentType<{ href: string; children: ReactNode }>;

const PlainLink: LinkComponent = ({ href, children }) => (
  <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>
);

const isLinked = (v: ComposedValue | LinkedValue): v is LinkedValue =>
  Array.isArray((v as LinkedValue).link);

/**
 * Render one resolved copy value.
 *
 * A span whose offsets do not fit the text renders as plain text rather than
 * throwing. `resolveCopySlot` already refuses a value whose span is out of
 * range, so reaching that branch means something upstream changed — and a
 * family reading a stop screen should get the sentence without its emphasis,
 * not a blank page.
 */
export function CopySpan({
  value,
  link: Link = PlainLink,
}: {
  value: RenderableCopy;
  link?: LinkComponent;
}) {
  if (typeof value === 'string') return <>{value}</>;

  const { text } = value;
  const span = isLinked(value) ? value.link : value.emphasis;
  if (!span) return <>{text}</>;

  const [start, end] = span;
  if (!(start >= 0 && start < end && end <= text.length)) return <>{text}</>;

  const before = text.slice(0, start);
  const inner = text.slice(start, end);
  const after = text.slice(end);

  return (
    <>
      {before}
      {isLinked(value) ? <Link href={value.href}>{inner}</Link> : <em>{inner}</em>}
      {after}
    </>
  );
}
